import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { cacheGet, cacheSet, queueEntry, getPending, listPending, notifyQueueChanged, uploadLocalPhotos, queueReport } from "../lib/offline";
import { buildLocalReport } from "../lib/offlineReport";
import { useAuth } from "../context/AuthContext";
import { EntryPhotos } from "../components/EntryPhotos";
import { AreasTable } from "../components/AreasTable";
import { ActivitiesTable, ChallengesTable } from "../components/StructuredLists";
import { Save, Loader2, CalendarDays, CalendarRange, FileText, Sparkles, Lock, Unlock, WifiOff, FileUp, ChevronDown, HelpCircle } from "lucide-react";

const FREQ_BADGE = {
  daily: { label: "Quotidien", cls: "bg-[#FFF3EE] text-[#F05A28]" },
  weekly: { label: "Hebdomadaire", cls: "bg-[#E0F2FE] text-[#0284C7]" },
  monthly: { label: "Mensuel", cls: "bg-slate-100 text-slate-600" },
  cumul: { label: "Cumul", cls: "bg-violet-50 text-violet-700" },
};
const OPEN_KEY = "stride_entry_open_groups";

export default function UnifiedEntry() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [fw, setFw] = useState(null);
  const [values, setValues] = useState({});
  const [narratives, setNarratives] = useState({});
  const [confidential, setConfidential] = useState({});
  const [photos, setPhotos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [activities, setActivities] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [refDate, setRefDate] = useState("");
  const [cumulDate, setCumulDate] = useState("");
  const [openGroups, setOpenGroups] = useState(() => { try { return JSON.parse(localStorage.getItem(OPEN_KEY)) || { alertes: true, cas: true }; } catch { return { alertes: true, cas: true }; } });
  const toggleGroup = (id) => { const n = { ...openGroups, [id]: !openGroups[id] }; setOpenGroups(n); localStorage.setItem(OPEN_KEY, JSON.stringify(n)); };
  const toggleUnknown = (code) => {
    const on = unknown.includes(code);
    setUnknown(on ? unknown.filter((c) => c !== code) : [...unknown, code]);
    if (!on) { const v = { ...values }; delete v[code]; setValues(v); }
  };
  const applySrc = (src) => {
    setAreas(src?.areas || []); setActivities(src?.activities || []); setChallenges(src?.challenges || []);
    setUnknown(src?.unknown || []); setRefDate(src?.data_reference_date || ""); setCumulDate(src?.cumul_reference_date || "");
  };
  const [localPhotos, setLocalPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [gen, setGen] = useState("");
  const [local, setLocal] = useState("");
  const [importing, setImporting] = useState(false);

  const importDoc = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/harmonized/entry/import-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setValues((v) => ({ ...v, ...Object.fromEntries(Object.entries(d.values || {}).map(([k, x]) => [k, String(x)])) }));
      setNarratives((n) => ({ ...n, ...d.narratives }));
      if (d.areas?.length) setAreas(d.areas);
      if (d.date && d.date <= today) setDay(d.date);
      toast.success(`${Object.keys(d.values || {}).length} ${t("chiffres et")} ${Object.keys(d.narratives || {}).length} ${t("textes pré-remplis. Vérifiez puis enregistrez.")}`);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || t("Import impossible."));
    } finally { setImporting(false); }
  };

  const load = useCallback(async (d) => {
    try {
      const [f, e] = await Promise.all([
        api.get("/harmonized/framework", { params: { lang } }),
        api.get("/harmonized/entry", { params: { date: d } }),
      ]);
      setFw(f.data);
      await cacheSet(`framework:${lang}`, f.data);
      api.get("/harmonized/entries/full").then((r) => cacheSet(`entries:${f.data.volet}`, r.data)).catch(() => null);
      const q = await getPending(d, f.data.volet);
      const src = q ? q.payload : e.data;
      setValues(src?.values || {});
      setNarratives(src?.narratives || {});
      setConfidential(src?.confidential || {});
      setPhotos(src?.photos || []);
      applySrc(src);
      setLocalPhotos(q?.payload?.local_photos || []);
      setLocal(q ? q.at : "");
    } catch (err) {
      if (err.response) throw err;
      const cached = await cacheGet(`framework:${lang}`);
      if (!cached?.value) {
        toast.error(t("Hors ligne et formulaire non encore téléchargé. Connectez-vous une fois au réseau."));
        return;
      }
      setFw(cached.value);
      const q = await getPending(d, cached.value.volet);
      setValues(q?.payload?.values || {});
      setNarratives(q?.payload?.narratives || {});
      setConfidential(q?.payload?.confidential || {});
      setPhotos(q?.payload?.photos || []);
      applySrc(q?.payload);
      setLocalPhotos(q?.payload?.local_photos || []);
      setLocal(q ? q.at : "");
      toast.message(t("Mode hors ligne · formulaire chargé depuis l'appareil."));
    }
  }, [lang, t]);

  useEffect(() => { load(day).catch((err) => toast.error(apiErr(err.response?.data?.detail))); }, [day, load]);

  useEffect(() => {
    const clear = () => getPending(day, fw?.volet).then((q) => setLocal(q ? q.at : ""));
    window.addEventListener("stride-queue-changed", clear);
    return () => window.removeEventListener("stride-queue-changed", clear);
  }, [day, fw]);

  const extra = () => ({ areas, activities, challenges, unknown, data_reference_date: refDate || day, cumul_reference_date: cumulDate });
  const payload = () => ({ date: day, volet: fw.volet, values, narratives, confidential, photos, ...extra(), local_photos: localPhotos });

  const saveOffline = async () => {
    await queueEntry(payload());
    notifyQueueChanged();
    setLocal(new Date().toISOString());
    toast.success(t("Saisie enregistrée sur l'appareil. Elle sera envoyée au retour du réseau."));
  };

  const pushOnline = async () => {
    const uploaded = await uploadLocalPhotos(api, localPhotos);
    const all = [...photos, ...uploaded];
    await api.post("/harmonized/entry", { date: day, volet: fw.volet, values, narratives, confidential, photos: all, ...extra() });
    setPhotos(all);
    setLocalPhotos([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!navigator.onLine) { await saveOffline(); return; }
      await pushOnline();
      setLocal("");
      toast.success(t("Saisie enregistrée."));
    } catch (err) {
      if (!err.response) await saveOffline();
      else toast.error(apiErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const generateOffline = async (ptype) => {
    await saveOffline();
    if (ptype === "monthly") { toast.error(t("Le rapport mensuel nécessite une connexion. Saisie conservée sur l'appareil.")); return; }
    const cached = await cacheGet(`entries:${fw.volet}`);
    const pend = await listPending();
    const mine = (e) => e.author_id === user?.id;
    const pendingDates = new Set(pend.map((q) => q.payload.date));
    const entries = [
      ...((cached?.value || []).filter((e) => !(mine(e) && pendingDates.has(e.date)))),
      ...pend.map((q) => ({ ...q.payload, author_id: user?.id, author_name: user?.name })),
    ];
    const report = buildLocalReport(ptype, day, fw, entries, user);
    if (!report) { toast.error(t("Aucune saisie quotidienne trouvée pour cette période.")); return; }
    await queueReport(report);
    notifyQueueChanged();
    toast.success(t("Rapport généré hors ligne. Il sera envoyé au serveur à la reconnexion."));
    navigate(`/generated/${report.id}`);
  };

  const generate = async (ptype) => {
    if (!navigator.onLine) { await generateOffline(ptype); return; }
    setGen(ptype);
    try {
      await pushOnline();
      const d = new Date(day);
      const body = ptype === "daily" ? { ptype, date: day }
        : ptype === "monthly" ? { ptype, year: d.getFullYear(), month: d.getMonth() + 1 }
          : { ptype };
      const res = await api.post("/harmonized/generate", { ...body, volet: fw.volet });
      toast.success(t("Brouillon généré."));
      navigate(ptype === "monthly" ? `/monthly/${res.data.id}` : `/generated/${res.data.id}`);
    } catch (err) {
      if (!err.response) await generateOffline(ptype);
      else toast.error(apiErr(err.response?.data?.detail));
    } finally { setGen(""); }
  };

  if (!fw) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#F05A28]" /></div>;

  const isWeekEnd = new Date(day).getDay() === 0;
  const dObj = new Date(day);
  const isMonthEnd = new Date(dObj.getFullYear(), dObj.getMonth() + 1, 0).getDate() === dObj.getDate();

  return (
    <div data-testid="unified-entry-page">
      <PageHeader eyebrow={t("Rapport du jour")} title={t("Ma journée")}
        subtitle={t("Saisissez vos chiffres et vos notes du jour : les rapports quotidien, hebdomadaire et mensuel sont produits automatiquement.")}
        testid="unified-header">
        <Button onClick={save} disabled={saving} className="w-full sm:w-auto h-11 sm:h-10 rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="entry-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}{t("Enregistrer")}
        </Button>
      </PageHeader>

      {local && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-100 px-4 py-2.5 text-xs text-slate-700" data-testid="entry-local-banner">
          <WifiOff className="h-3.5 w-3.5" />
          {t("Enregistré sur l'appareil, en attente d'envoi")} · {new Date(local).toLocaleString()}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="entry-dates">
        <div>
          <Label className="text-xs">{t("Date du rapport")}</Label>
          <Input type="date" className="h-11 sm:h-10 text-base sm:text-sm" value={day} max={today} onChange={(e) => setDay(e.target.value)} data-testid="entry-date" />
        </div>
        <div>
          <Label className="text-xs">{t("Date des chiffres du jour")} <span className="text-slate-400">({t("si veille")})</span></Label>
          <Input type="date" className="h-11 sm:h-10 text-base sm:text-sm" value={refDate} max={day} onChange={(e) => setRefDate(e.target.value)} data-testid="entry-ref-date" />
        </div>
        <div>
          <Label className="text-xs">{t("Date de référence des cumuls (DHIS2)")}</Label>
          <Input type="date" className="h-11 sm:h-10 text-base sm:text-sm" value={cumulDate} max={day} onChange={(e) => setCumulDate(e.target.value)} data-testid="entry-cumul-date" />
          {cumulDate && (new Date(day) - new Date(cumulDate)) / 86400000 > 3 && <p className="text-[11px] text-amber-700 mt-1" data-testid="entry-cumul-stale">{t("Cumuls datés de plus de 3 jours : à actualiser.")}</p>}
        </div>
        <div className="sm:col-span-2 text-xs text-slate-500">
          <p className="font-semibold text-slate-700">{fw.label}</p>
          <p>{t("Laissez vide ce que vous n'avez pas. Les champs hebdomadaires et mensuels ne sont à remplir qu'en fin de période.")} {t("Marquez « ? » une donnée réellement inconnue : elle ne sera pas comptée comme zéro.")}</p>
        </div>
          <label className={`inline-flex items-center justify-center gap-1.5 h-11 sm:h-10 px-3 rounded-md border border-dashed text-sm font-medium cursor-pointer transition-colors ${navigator.onLine ? "border-[#F05A28] text-[#F05A28] hover:bg-[#FFF3EE]" : "border-slate-300 text-slate-400 cursor-not-allowed"}`} data-testid="entry-import-doc" onClick={(e) => { e.preventDefault(); navigate("/import-word"); }}>
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          {t("Importer un rapport Word")}
          <input type="file" accept=".docx,.doc,.pdf,.txt" className="hidden" disabled={importing || !navigator.onLine}
            onChange={(e) => { importDoc(e.target.files?.[0]); e.target.value = ""; }} data-testid="entry-import-doc-input" />
        </label>
      </div>

      <div className="space-y-4">
        {fw.groups.map((g) => {
          const filled = g.fields.filter((f) => values[f.code] !== undefined && values[f.code] !== "").length;
          const isOpen = !!openGroups[g.id];
          return (
          <section key={g.id} className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm" data-testid={`entry-group-${g.id}`}>
            <button type="button" onClick={() => toggleGroup(g.id)} className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left" data-testid={`entry-group-toggle-${g.id}`}>
              <h2 className="text-sm font-semibold text-slate-900">{g.label}</h2>
              <span className="flex items-center gap-2 text-xs text-slate-500"><span data-testid={`entry-group-count-${g.id}`}>{filled}/{g.fields.length}</span><ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></span>
            </button>
            {isOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-4 sm:px-5 pb-5">
              {g.fields.map((f) => {
                const b = FREQ_BADGE[f.freq] || FREQ_BADGE.daily;
                const dim = (f.freq === "weekly" && !isWeekEnd) || (f.freq === "monthly" && !isMonthEnd);
                const unk = unknown.includes(f.code);
                return (
                  <div key={f.code} className={dim ? "opacity-60" : ""}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Label className="text-xs">{f.label}</Label>
                      <span className="flex items-center gap-1">
                        <span className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${b.cls}`}>{t(b.label)}</span>
                        <button type="button" onClick={() => toggleUnknown(f.code)} title={t("Donnée inconnue")} data-testid={`entry-unknown-${f.code}`}
                          className={`h-5 w-5 rounded inline-flex items-center justify-center ${unk ? "bg-[#0F172A] text-white" : "text-slate-300 hover:text-slate-500"}`}><HelpCircle className="h-3.5 w-3.5" /></button>
                      </span>
                    </div>
                    <Input type="number" min="0" inputMode="numeric" className="h-11 sm:h-10 text-base sm:text-sm" value={unk ? "" : (values[f.code] ?? "")} disabled={unk} placeholder={unk ? t("Inconnu") : ""}
                      onChange={(e) => setValues({ ...values, [f.code]: e.target.value })}
                      data-testid={`entry-field-${f.code}`} />
                  </div>
                );
              })}
            </div>
            )}
          </section>
          );
        })}

        <AreasTable areas={areas} onChange={setAreas} />
        <ActivitiesTable activities={activities} categories={fw.activity_categories || []} onChange={setActivities} />
        <ChallengesTable challenges={challenges} onChange={setChallenges} />

        <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5" data-testid="entry-narratives">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">{t("Notes et analyse du jour")}</h2>
          <p className="text-xs text-slate-500 mb-4">{t("Ces notes sont reportées automatiquement, par thème, dans les rapports hebdomadaire et mensuel. Une note marquée « Confidentiel » reste visible uniquement par vous, votre superviseur provincial et l'Analyste SI : elle est exclue des rapports agrégés et des exports.")}</p>
          <div className="space-y-4">
            {fw.narratives.map((n) => {
              const conf = !!confidential[n.code];
              return (
                <div key={n.code}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Label className="text-xs">{n.label}</Label>
                    <button type="button" onClick={() => setConfidential({ ...confidential, [n.code]: !conf })}
                      data-testid={`entry-conf-${n.code}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded transition-colors ${conf ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                      {conf ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                      {t(conf ? "Confidentiel" : "Partagé")}
                    </button>
                  </div>
                  <Textarea rows={3} className="text-base sm:text-sm" value={narratives[n.code] || ""}
                    onChange={(e) => setNarratives({ ...narratives, [n.code]: e.target.value })}
                    data-testid={`entry-narr-${n.code}`} />
                </div>
              );
            })}
          </div>
        </section>

        <EntryPhotos photos={photos} localPhotos={localPhotos}
          onChange={(p, l) => { setPhotos(p); setLocalPhotos(l); }} />

        <section className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5" data-testid="entry-generate">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="h-4 w-4 text-[#F05A28] mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900">{t("Générer un produit de rapportage")}</h2>
              <p className="text-xs text-slate-500 mt-1">{t("Les brouillons sont aussi générés automatiquement (fin de journée, fin de semaine épidémiologique, fin de mois) et doivent être validés et signés avant soumission.")} {t("Hors ligne, les rapports quotidien et hebdomadaire sont calculés sur l'appareil puis envoyés au serveur à la reconnexion.")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
            <Button onClick={() => generate("daily")} disabled={!!gen} className="h-11 sm:h-10 rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="gen-daily-btn">
              {gen === "daily" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarDays className="h-4 w-4 mr-1.5" />}{t("Rapport quotidien")}
            </Button>
            <Button onClick={() => generate("weekly")} disabled={!!gen} variant="outline" className="h-11 sm:h-10 rounded-md" data-testid="gen-weekly-btn">
              {gen === "weekly" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-1.5" />}{t("Rapport hebdomadaire")}
            </Button>
            <Button onClick={() => generate("monthly")} disabled={!!gen} variant="outline" className="h-11 sm:h-10 rounded-md" data-testid="gen-monthly-btn">
              {gen === "monthly" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}{t("Rapport mensuel")}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
